import { Role, rolesManipulator } from 'auth';
import copy from 'copy-to-clipboard';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  mapUsers,
  UsersSelectPaginated,
} from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import CreateSecretModal from '@/modules/common/components/modals/CreateSecretModal.tsx';
import EditSecretModal from '@/modules/common/components/modals/EditSecretModal.tsx';
import EditVaultModal from '@/modules/common/components/modals/EditVaultModal.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import {
  useCreateSecretMutation,
  useDeleteSecretMutation,
  useUpdateSecretMutation,
  useDeleteVaultMutation,
  useShareVaultMutation,
  useUpdateVaultMutation,
  useVaultQuery,
  useVaultSecretsQuery,
} from '@/modules/common/queries/secret-manager.ts';
import {
  Avatar,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuItem,
  EmptyListState,
  ModalProviderContext,
  PageLayout,
  SearchField,
  SelectPaginateOption,
  Spinner,
  TabItem,
  Tabs,
  TextWithShortLinks,
} from 'ui';
import {
  ArrowLeftIcon,
  CopyIcon,
  DotsHorizontalIcon,
  EditGrayIcon,
  EyeIcon,
  EyeOffIcon,
  TrashGrayIcon,
} from 'ui/icons';

const TAB_SECRETS = 'secrets';
const TAB_ACCESS = 'access';
const DESCRIPTION_SEPARATOR_MAX_LENGTH = 140;

const SecretManagerVault = () => {
  const role = rolesManipulator.current_user_role as Role | undefined;
  const isAdmin = role === Role.COMPLIANCE_ADMIN;
  const navigate = useNavigate();
  const { openModal } = useContext(ModalProviderContext);
  const { vaultId } = useParams();
  const businessId = useGetCurrentBusinessId();

  const parsedVaultId = Number(vaultId);
  const isValidVaultId = Number.isInteger(parsedVaultId) && parsedVaultId > 0;

  const [activeTab, setActiveTab] = useState<string>(TAB_SECRETS);
  const [secretSearch, setSecretSearch] = useState('');
  const [visibleSecretIds, setVisibleSecretIds] = useState<Record<number, boolean>>({});
  const [selectedSharedUsers, setSelectedSharedUsers] = useState<SelectPaginateOption<number>[]>([]);

  const { data: vaultData, isLoading: isVaultLoading } = useVaultQuery(
    { vaultId: parsedVaultId },
    { enabled: isValidVaultId },
  );

  const vault = vaultData?.vault;

  const {
    data: secretsData,
    isLoading: isSecretsLoading,
    isFetching: isSecretsFetching,
    refetch: refetchSecrets,
  } = useVaultSecretsQuery(
    {
      vaultId: parsedVaultId,
      page: 1,
      page_size: 100,
      search: secretSearch || undefined,
    },
    { enabled: isValidVaultId },
  );

  const shareVaultMutation = useShareVaultMutation();
  const createSecretMutation = useCreateSecretMutation();
  const updateSecretMutation = useUpdateSecretMutation();
  const deleteSecretMutation = useDeleteSecretMutation();
  const updateVaultMutation = useUpdateVaultMutation();
  const deleteVaultMutation = useDeleteVaultMutation();

  const handleBack = () => {
    navigate(isAdmin ? '/admin/secret-manager' : '/employee/secret-manager');
  };

  const handleSaveSharing = useCallback(async () => {
    if (!isValidVaultId) return;

    await shareVaultMutation.mutateAsync({
      vault_id: parsedVaultId,
      shared_user_ids: selectedSharedUsers.map(user => user.value),
    });
  }, [isValidVaultId, parsedVaultId, selectedSharedUsers, shareVaultMutation]);

  const handleOpenCreateSecretModal = () => {
    if (!isValidVaultId || !vault) {
      toast.error('Vault not found');
      return;
    }

    openModal(CreateSecretModal, {
      vaultName: vault.name,
      onCreate: async payload => {
        await createSecretMutation.mutateAsync({
          vault_id: parsedVaultId,
          name: payload.name,
          value: payload.value,
        });
        setSecretSearch('');
        await refetchSecrets();
      },
    });
  };

  const copySecretValue = (value: string) => {
    copy(value);
    toast.success('Copied to clipboard');
  };

  const toggleSecretVisibility = (secretId: number) => {
    setVisibleSecretIds(prev => ({ ...prev, [secretId]: !prev[secretId] }));
  };

  const handleEditVault = () => {
    if (!vault) {
      toast.error('Vault not found');
      return;
    }

    openModal(EditVaultModal, {
      vault,
      onSave: async payload => {
        await updateVaultMutation.mutateAsync(payload);
      },
    });
  };

  const handleEditSecret = useCallback(
    (secret: { id: number; name: string; value: string }) => {
      if (!isValidVaultId || !vault) {
        toast.error('Vault not found');
        return;
      }

      openModal(EditSecretModal, {
        vaultName: vault.name,
        secretName: secret.name,
        secretValue: secret.value,
        onSave: async payload => {
          await updateSecretMutation.mutateAsync({
            vault_id: parsedVaultId,
            secret_id: secret.id,
            name: payload.name,
            value: payload.value,
          });
          await refetchSecrets();
        },
      });
    },
    [isValidVaultId, openModal, parsedVaultId, refetchSecrets, updateSecretMutation, vault],
  );

  const handleDeleteSecret = useCallback((secret: { id: number; name: string }) => {
    if (!isValidVaultId) return;

    openModal(ConfirmModal, {
      onConfirm: async () => {
        await deleteSecretMutation.mutateAsync({
          vault_id: parsedVaultId,
          secretId: secret.id,
        });
        await refetchSecrets();
      },
      title: (
        <p>
          Are you sure you want to delete <span className="text-blue-700">{secret.name}</span>?
        </p>
      ),
    });
  }, [deleteSecretMutation, isValidVaultId, openModal, parsedVaultId, refetchSecrets]);

  const handleDeleteVault = () => {
    if (!vault) {
      toast.error('Vault not found');
      return;
    }

    openModal(ConfirmModal, {
      onConfirm: async () => {
        await deleteVaultMutation.mutateAsync({ vaultId: vault.id });
        handleBack();
      },
      title: (
        <p>
          Are you sure you want to delete <span className="text-blue-700">{vault.name}</span>?
        </p>
      ),
    });
  };

  const menuOptions: DropdownMenuItem[] = [
    {
      label: 'Edit',
      onClick: handleEditVault,
    },
    {
      label: 'Delete',
      onClick: handleDeleteVault,
    },
  ];

  const title = (
    <div className="flex items-center gap-3 min-w-0 w-full">
      <button
        onClick={handleBack}
        className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 transition-colors"
        aria-label="Go back"
      >
        <img src={ArrowLeftIcon} alt="Back" className="w-6 h-6" />
      </button>
      <span className="text-2xl font-semibold text-gray-900 truncate font-sans min-w-0 flex-1">
        {vault?.name || 'Vault'}
      </span>
      {isAdmin && vault && (
        <DropdownMenu
          autoPlace
          items={menuOptions}
          button={
            <button className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 transition-colors">
              <img src={DotsHorizontalIcon} alt="Menu" className="w-6 h-6" />
            </button>
          }
          className="w-[160px] divide-white"
          containerClassName="w-auto"
        />
      )}
    </div>
  );

  const headerAction = (
    <div className="w-full min-w-0 mt-0 flex flex-col sm:flex-row justify-end sm:items-center gap-3">
      {activeTab === TAB_SECRETS && (
        <SearchField
          placeholder="Search secrets"
          value={secretSearch}
          onChange={setSecretSearch}
          className="w-full min-w-0 sm:w-[260px]"
        />
      )}
      {isAdmin && activeTab === TAB_SECRETS && (
        <div className="w-full sm:w-fit">
          <Button
            variant="primary"
            className="w-full min-w-0 sm:w-fit"
            onClick={handleOpenCreateSecretModal}
          >
            Create secret
          </Button>
        </div>
      )}
    </div>
  );

  const headerBottomContent =
    vault && !isVaultLoading ? (
      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
        {vault.owner && (
          <div className="flex items-center gap-2">
            <Avatar
              size="md"
              src={vault.owner.avatar_url || undefined}
              firstName={vault.owner.first_name}
              lastName={vault.owner.last_name}
            />
            <span>
              Owner: {vault.owner.first_name} {vault.owner.last_name}
            </span>
          </div>
        )}
        {!!vault.description && (
          <div
            className={`inline-flex items-center gap-2 ${
              vault.description.length > DESCRIPTION_SEPARATOR_MAX_LENGTH
                ? ''
                : 'pl-3 border-l border-gray-200'
            }`}
          >
            <TextWithShortLinks
              text={vault.description}
              className="break-all text-gray-600 max-h-[72px] overflow-y-auto pr-1"
            />
          </div>
        )}
      </div>
    ) : undefined;

  const tabItems: TabItem[] = useMemo(() => {
    const items: TabItem[] = [
      {
        name: TAB_SECRETS,
        label: 'Secrets',
        container:
          isSecretsLoading || isSecretsFetching ? (
            <div className="w-full py-8 flex justify-center">
              <Spinner size="md" />
            </div>
          ) : (
            <div className="space-y-2 min-h-[220px]">
              {(secretsData?.data || []).map(secret => {
                const isVisible = !!visibleSecretIds[secret.id];
                return (
                  <div key={secret.id} className="border border-gray-200 rounded-xl bg-white">
                    <div className="px-3 py-2.5 sm:px-4 sm:py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-medium text-sm text-gray-900 min-w-0 flex-1">
                          <TextWithShortLinks text={secret.name} className="break-all" />
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => toggleSecretVisibility(secret.id)}
                            className="flex items-center justify-center sm:justify-start w-7 h-7 sm:w-auto sm:h-auto gap-1 text-xs text-gray-700 hover:text-gray-900 shrink-0"
                            aria-label={isVisible ? 'Hide secret value' : 'Show secret value'}
                            title={isVisible ? 'Hide' : 'Show'}
                          >
                            <img
                              src={isVisible ? EyeOffIcon : EyeIcon}
                              alt={isVisible ? 'hide' : 'show'}
                              className="w-4 h-4 min-w-4 min-h-4 shrink-0"
                            />
                            <span className="hidden sm:inline">{isVisible ? 'Hide' : 'Show'}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => copySecretValue(secret.value)}
                            className="flex items-center justify-center sm:justify-start w-7 h-7 sm:w-auto sm:h-auto gap-1 text-xs text-blue-700 hover:text-blue-800 shrink-0"
                            aria-label="Copy secret value"
                            title="Copy"
                          >
                            <img src={CopyIcon} alt="copy" className="w-4 h-4 min-w-4 min-h-4 shrink-0" />
                            <span className="hidden sm:inline">Copy</span>
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleEditSecret(secret)}
                                className="flex items-center justify-center sm:justify-start w-7 h-7 sm:w-auto sm:h-auto gap-1 text-xs text-gray-700 hover:text-gray-900 shrink-0"
                                aria-label="Edit secret"
                                title="Edit"
                              >
                                <img src={EditGrayIcon} alt="edit" className="w-4 h-4 min-w-4 min-h-4 shrink-0" />
                                <span className="hidden sm:inline">Edit</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteSecret(secret)}
                                className="flex items-center justify-center sm:justify-start w-7 h-7 sm:w-auto sm:h-auto gap-1 text-xs text-red-600 hover:text-red-700 shrink-0"
                                aria-label="Delete secret"
                                title="Delete"
                              >
                                <img
                                  src={TrashGrayIcon}
                                  alt="delete"
                                  className="w-4 h-4 min-w-4 min-h-4 shrink-0"
                                />
                                <span className="hidden sm:inline">Delete</span>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <pre className="mt-2 text-xs whitespace-pre-wrap break-words text-gray-700 bg-gray-50 rounded p-2">
                        {isVisible ? secret.value : '•'.repeat(24)}
                      </pre>
                    </div>
                  </div>
                );
              })}
              <div className="mt-20" />
              <EmptyListState
                title="No passwords added"
                description={
                  isAdmin
                    ? 'Click Create secret to add a password/secret to this vault.'
                    : 'There are no secrets in this vault yet.'
                }
                itemsCount={(secretsData?.data || []).length}
                isLoading={false}
                isSearchUsed={!!secretSearch}
              />
            </div>
          ),
      },
    ];

    if (isAdmin) {
      items.push({
        name: TAB_ACCESS,
        label: 'Access settings',
        container: (
          <div className=" p-4 rounded-xl border border-gray-200 bg-white space-y-4">
            <UsersSelectPaginated
              isMulti
              label="Other users with access:"
              placeholder="No one except you has access. Click to select users"
              value={selectedSharedUsers}
              onChange={options => setSelectedSharedUsers(options || [])}
              isClearable
              excludeUserIds={vault?.owner?.id ? [vault.owner.id] : []}
            />
            <div className="flex justify-end">
              <Button
                variant="primary"
                className="w-fit"
                onClick={handleSaveSharing}
                disabled={shareVaultMutation.isLoading}
              >
                {shareVaultMutation.isLoading ? 'Saving...' : 'Save access settings'}
              </Button>
            </div>
          </div>
        ),
      });
    }

    return items;
  }, [
    isAdmin,
    isSecretsFetching,
    isSecretsLoading,
    handleDeleteSecret,
    handleEditSecret,
    handleSaveSharing,
    secretSearch,
    secretsData?.data,
    selectedSharedUsers,
    shareVaultMutation.isLoading,
    vault?.owner?.id,
    visibleSecretIds,
  ]);

  useEffect(() => {
    setActiveTab(TAB_SECRETS);
    setSecretSearch('');
    setVisibleSecretIds({});
    setSelectedSharedUsers([]);
  }, [businessId]);

  useEffect(() => {
    setSelectedSharedUsers(
      mapUsers((vault?.shared_users || []).filter(user => user.id !== vault?.owner?.id)),
    );
  }, [vault?.id, vault?.shared_users, vault?.owner?.id]);

  if (!isValidVaultId) {
    return (
      <PageLayout title="Vault">
        <div className="w-full py-16 text-center text-gray-600">Invalid vault id</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={title}
      headerAction={headerAction}
      tabs={tabItems}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabNameParam="tab"
      contentClassName="!p-0"
      classNameTitle="flex-col items-stretch gap-3 sm:flex-row sm:justify-between sm:items-center [&>div:first-child]:flex-1"
      headerBottomContent={headerBottomContent}
    >
      {isVaultLoading ? (
        <div className="w-full py-12 flex justify-center">
          <Spinner size="md" />
        </div>
      ) : !vault ? (
        <div className="w-full py-16 text-center text-gray-600">Vault not found</div>
      ) : (
        <div className="px-3 sm:px-6 py-4 sm:py-6">
          <Tabs items={tabItems} activeTab={activeTab} />
        </div>
      )}
    </PageLayout>
  );
};

export default SecretManagerVault;
